import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FaCheck, FaCheckDouble, FaFacebookF, FaFileAlt, FaImage, FaInstagram, FaMicrophone, FaPaperPlane, FaPaperclip, FaPlus, FaSearch, FaSmile, FaStop, FaUsers, FaWhatsapp } from "react-icons/fa";
import { FiBriefcase, FiChevronLeft, FiDownload, FiEdit2, FiInfo, FiRefreshCw, FiX } from "react-icons/fi";
import "./Communication.css";
import "./WhatsAppInbox.css";
import "./WhatsAppExperience.css";
import "./WhatsAppRefinement.css";
import { formatDateTimeUS } from "../../utils/dateFormat";

// La ruta relativa usa el proxy de CRA en local y el mismo origen en el túnel
// público. Evita que el navegador intente resolver “localhost” en otro equipo.
const ROOT_API = window.__CRM_API__ || `${window.location.origin}/api`;
const API = `${ROOT_API}/communication`;
const FILE_ROOT = ROOT_API.replace(/\/api$/, "");
const TEMPLATE_PRIORITY = ["solicitud_documentos_tramite", "seguimiento_asunto_legal", "contacto_quiroz_law_firm"];
const UTILITY_TEMPLATE_LABELS = {
  solicitud_documentos_tramite: "Solicitar documentos o información",
  recordatorio_cita_legal: "Recordar una cita",
  actualizacion_caso_legal: "Compartir actualización del caso",
};
const templateLanguageCode = (value) => String(value || "es").trim() || "es";
const templateLanguageLabel = (value) => {
  const normalized = templateLanguageCode(value).toLowerCase().replaceAll("_", "-");
  if (normalized === "es" || normalized.startsWith("es-")) return "Español";
  if (normalized === "en" || normalized.startsWith("en-")) return "English";
  return templateLanguageCode(value);
};
const isSpanishTemplateLanguage = (value) => templateLanguageCode(value).toLowerCase().startsWith("es");
const templateBody = (template) =>
  template?.components?.find((component) => String(component.type || "").toUpperCase() === "BODY")?.text || "";
const templateVariables = (template) => {
  const matches = [...templateBody(template).matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map((match) => match[1]);
  return [...new Set(matches)];
};
const templateFieldLabel = (variable, index, templateName) => {
  const labels = {
    nombre_cliente: "Nombre del contacto",
    documentos_solicitados: "Documentos o información solicitada",
  };
  const labelsByTemplate = {
    solicitud_documentos_tramite: ["Nombre del contacto", "Documentos o información solicitada"],
    recordatorio_cita_legal: ["Nombre del contacto", "Fecha de la cita", "Hora de la cita", "Modalidad o lugar"],
    actualizacion_caso_legal: ["Nombre del contacto", "Referencia del caso", "Información o actualización"],
  };
  return labels[variable] || labelsByTemplate[templateName]?.[index] || (index === 0 ? "Nombre del contacto" : `Dato ${index + 1}`);
};
// AuthContext configura el JWT global de axios para toda sesión autenticada.
// No lo sobrescribimos aquí: así QLF Conecta comparte exactamente la misma
// sesión que los demás Workspaces.
const auth = () => ({});
// A linked expediente is the CRM's authoritative identity.  Keep a provider
// nickname as fallback only; otherwise a WhatsApp address-book alias hides the
// client name even after the thread has been linked correctly.
const nameOf = (chat) => chat?.cliente_nombre || chat?.lead_nombre || chat?.nombre_contacto || chat?.telefono || "Contacto sin nombre";
const linkedEmail = (chat) => chat?.cliente_id ? chat?.cliente_email : chat?.lead_id ? chat?.lead_email : "";
const contactStatus = (chat) => chat?.cliente_id ? "Cliente vinculado" : chat?.lead_id ? "Lead vinculado" : "Contacto sin vincular";
const messageTime = (item) => new Date(item?.created_at || 0).getTime() || 0;
const normalizeSearchText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();
const conversationTime = (item) => new Date(item?.search_match_created_at || item?.ultimo_mensaje || item?.created_at || 0).getTime() || 0;
const newestFirst = (items, timeOf) =>
  [...items].sort((left, right) => timeOf(right) - timeOf(left) || Number(right.id) - Number(left.id));
const oldestFirst = (items, timeOf) =>
  [...items].sort((left, right) => timeOf(left) - timeOf(right) || Number(left.id) - Number(right.id));
const dateTime = (value) => formatDateTimeUS(value, "");
const noteDateTime = (value) => formatDateTimeUS(value, "");
const currentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("usuario") || sessionStorage.getItem("usuario") || "{}");
  } catch {
    return {};
  }
};

function NewWhatsAppChat({ onClose, onCreated }) {
  const [form, setForm] = useState({
    nombre_contacto: "",
    telefono: "",
    es_consulado: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const phone = form.telefono.replace(/[^0-9+]/g, "");
      if (!phone) throw new Error("Ingresa un número de WhatsApp válido.");
      const { data } = await axios.post(
        `${API}/conversations`,
        {
          canal: "whatsapp",
          telefono: phone,
          nombre_contacto: form.nombre_contacto.trim() || null,
          es_consulado: form.es_consulado,
        },
        auth(),
      );
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "No fue posible crear el chat.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="comm-modal-backdrop" onMouseDown={onClose}>
      <form className="comm-modal" onMouseDown={(event) => event.stopPropagation()} onSubmit={submit}>
        <header>
          <div>
            <span className="modal-eyebrow">WHATSAPP BUSINESS</span>
            <h2>Nuevo chat externo</h2>
            <p>Puede ser un contacto independiente, sin crear Lead ni Cliente.</p>
          </div>
          <button type="button" className="icon-close" onClick={onClose}>
            <FiX />
          </button>
        </header>
        <label>
          Nombre del contacto
          <input autoFocus value={form.nombre_contacto} onChange={(event) => setForm({ ...form, nombre_contacto: event.target.value })} placeholder="Ej. Consulado de Colombia" />
        </label>
        <label>
          WhatsApp / teléfono <span>*</span>
          <input required inputMode="tel" value={form.telefono} onChange={(event) => setForm({ ...form, telefono: event.target.value })} placeholder="+1 213 555 0123" />
        </label>
        <label className="consulate-toggle">
          <input type="checkbox" checked={form.es_consulado} onChange={(event) => setForm({ ...form, es_consulado: event.target.checked })} />
          <span>
            <FiBriefcase /> Marcar como conversación del Consulado
          </span>
        </label>
        {error && <p className="comm-error">{error}</p>}
        <footer>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={busy}>
            {busy ? "Creando…" : "Crear chat"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function WhatsAppWorkspace({ liteMode = false }) {
  return <ExternalChannelWorkspace liteMode={liteMode} />;
  /* Legacy inbox retained below as reference while the WhatsApp-style inbox is active.
  const [conversations, setConversations] = useState([]); const [selectedId, setSelectedId] = useState(null); const [messages, setMessages] = useState([]); const [draft, setDraft] = useState(''); const [search, setSearch] = useState(''); const [filter, setFilter] = useState('all'); const [loading, setLoading] = useState(true); const [sending, setSending] = useState(false); const [error, setError] = useState(''); const [newChat, setNewChat] = useState(false); const [infoOpen, setInfoOpen] = useState(false); const [moreOpen, setMoreOpen] = useState(false); const [isMobile, setIsMobile] = useState(() => window.matchMedia?.('(max-width: 760px)').matches || false); const [mobileChatOpen, setMobileChatOpen] = useState(false); const messageEnd = useRef(null);
  useEffect(() => { const media = window.matchMedia('(max-width: 760px)'); const update = () => { setIsMobile(media.matches); if (!media.matches) setMobileChatOpen(true); }; update(); media.addEventListener?.('change', update); return () => media.removeEventListener?.('change', update); }, []);
  const loadConversations = useCallback(async () => { try { setError(''); const response = await axios.get(`${API}/conversations`, { ...auth(), params: { canal: 'whatsapp', estado: null } }); setConversations(response.data); setSelectedId(current => current || response.data.find(item => item.estado === 'active')?.id || response.data[0]?.id || null); } catch (err) { setError(err.response?.data?.detail || 'No se pudo cargar WhatsApp Business.'); } finally { setLoading(false); } }, []);
  const loadMessages = useCallback(async id => { if (!id) return setMessages([]); try { const response = await axios.get(`${API}/messages/${id}`, { ...auth(), params: { limit: 100 } }); setMessages(response.data.reverse()); await axios.post(`${API}/conversations/${id}/read`, {}, auth()); setConversations(items => items.map(item => item.id === id ? { ...item, mensajes_no_leidos: 0 } : item)); } catch { setError('No se pudieron cargar los mensajes de este chat.'); } }, []);
  useEffect(() => { loadConversations(); }, [loadConversations]); useEffect(() => { loadMessages(selectedId); setInfoOpen(false); }, [selectedId, loadMessages]); useEffect(() => { if (messageEnd.current) messageEnd.current.scrollIntoView({ block: 'end' }); }, [messages]);
  useEffect(() => { const poll = window.setInterval(() => { loadConversations(); if (selectedId) loadMessages(selectedId); }, 12000); return () => window.clearInterval(poll); }, [loadConversations, loadMessages, selectedId]);
  const selected = conversations.find(item => item.id === selectedId);
  const filtered = useMemo(() => conversations.filter(chat => { const term = search.trim().toLowerCase(); const bySearch = !term || `${nameOf(chat)} ${chat.telefono || ''}`.toLowerCase().includes(term); const byFilter = filter === 'all' || (filter === 'unread' && chat.mensajes_no_leidos > 0) || (filter === 'closed' && chat.estado === 'closed'); return bySearch && byFilter; }), [conversations, search, filter]);
  const patchConversation = async changes => { if (!selected) return; try { const { data } = await axios.put(`${API}/conversations/${selected.id}`, changes, auth()); setConversations(items => items.map(item => item.id === selected.id ? data : item)); setMoreOpen(false); } catch (err) { setError(err.response?.data?.detail || 'No fue posible actualizar la conversación.'); } };
  const selectConversation = id => { setSelectedId(id); if (isMobile) setMobileChatOpen(true); };
  const send = async event => { event.preventDefault(); const content = draft.trim(); if (!content || !selectedId || sending) return; setSending(true); setError(''); try { const response = await axios.post(`${API}/messages`, { conversation_id: selectedId, contenido: content, tipo: 'text' }, auth()); if (!response.data.success) throw new Error(response.data.error || 'No se pudo enviar el mensaje.'); setDraft(''); await Promise.all([loadMessages(selectedId), loadConversations()]); } catch (err) { setError(err.response?.data?.detail || err.message || 'No se pudo enviar el mensaje.'); } finally { setSending(false); } };
  const onComposerKeyDown = event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } };
  const chatVisible = Boolean(selected && (!isMobile || mobileChatOpen));
  return <section className={`communication-workspace whatsapp-inbox ${chatVisible ? 'has-chat' : ''}`}>
    <aside className="comm-list" aria-label="Lista de conversaciones"><div className="comm-list-head"><div className="comm-channel-title"><FaWhatsapp /><span>WhatsApp</span></div><button className="comm-new" onClick={() => setNewChat(true)}><FaPlus /> <span>Nuevo chat</span></button></div><label className="comm-search"><FaSearch /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar o iniciar un chat" /></label><div className="comm-filters" role="tablist">{[['all','Todos'],['unread','No leídos'],['closed','Cerrados']].map(([key,label]) => <button key={key} type="button" className={filter === key ? 'active' : ''} onClick={() => setFilter(key)}>{label}</button>)}</div><div className="comm-rows">{loading && <p className="comm-empty">Cargando conversaciones…</p>}{!loading && !filtered.length && <p className="comm-empty">No hay conversaciones para este filtro.</p>}{filtered.map(chat => <button key={chat.id} onClick={() => selectConversation(chat.id)} className={`comm-row ${chat.id === selectedId ? 'active' : ''}`}><span className="comm-avatar">{nameOf(chat).charAt(0).toUpperCase()}</span><span className="comm-row-copy"><span><b>{nameOf(chat)}</b><time>{dateTime(chat.ultimo_mensaje)}</time></span><small>{chat.es_consulado ? '🇨🇴 Consulado · ' : ''}{contactMeta(chat)}{chat.estado === 'closed' ? ' · Cerrada' : ''}</small></span>{chat.mensajes_no_leidos > 0 && <i>{chat.mensajes_no_leidos}</i>}</button>)}</div></aside>
    <main className="comm-chat">{selected ? <><header className="comm-chat-head"><button type="button" className="comm-mobile-back" onClick={() => setSelectedId(null)} aria-label="Volver a conversaciones"><FiChevronLeft /></button><span className="comm-avatar big">{nameOf(selected).charAt(0).toUpperCase()}</span><div className="comm-contact-heading"><strong>{nameOf(selected)}</strong><small>{contactMeta(selected)}</small></div><span className="comm-contact-badge">{selected.cliente_id ? 'Cliente' : selected.lead_id ? 'Lead' : 'Sin vincular'}</span><div className="comm-head-actions"><button type="button" onClick={() => setInfoOpen(true)} title="Información del contacto" aria-label="Información del contacto"><FiInfo /></button><button type="button" onClick={() => setMoreOpen(value => !value)} title="Más acciones" aria-label="Más acciones"><FiMoreHorizontal /></button>{moreOpen && <div className="comm-menu"><button type="button" onClick={() => patchConversation({ es_consulado: !selected.es_consulado })}><FiBriefcase /> {selected.es_consulado ? 'Quitar Consulado' : 'Marcar Consulado'}</button>{selected.estado === 'closed' ? <button type="button" onClick={() => patchConversation({ estado: 'active' })}>Reabrir conversación</button> : <button type="button" onClick={() => patchConversation({ estado: 'closed' })}><FiXCircle /> Cerrar conversación</button>}<button type="button" onClick={() => patchConversation({ estado: 'archived' })}><FiArchive /> Archivar</button></div>}</div></header><div className="comm-thread">{!messages.length && <p className="comm-empty">El historial aparecerá aquí cuando exista comunicación.</p>}{messages.map((message, index) => <React.Fragment key={message.id}>{(!index || new Date(messages[index - 1].created_at).toDateString() !== new Date(message.created_at).toDateString()) && <span className="comm-date-divider">{new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(message.created_at))}</span>}<article className={`comm-bubble ${message.direccion === 'outgoing' ? 'out' : 'in'}`}><p>{message.contenido}</p><footer>{message.direccion === 'outgoing' && <span>Enviado por {message.autor_nombre || 'Equipo QLF'}</span>}<time>{dateTime(message.created_at)} {message.direccion === 'outgoing' ? '✓' : ''}</time></footer></article></React.Fragment>)}<span ref={messageEnd} /></div>{selected.estado === 'closed' ? <div className="comm-closed"><FiXCircle /> Conversación cerrada. <button type="button" onClick={() => patchConversation({ estado: 'active' })}>Reabrir para responder</button></div> : <form className="comm-compose" onSubmit={send}><span className="comm-compose-note">WhatsApp Business</span><textarea value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={onComposerKeyDown} placeholder="Escribe un mensaje" rows="1" /><button disabled={!draft.trim() || sending} title="Enviar mensaje"><FaPaperPlane /><span>{sending ? 'Enviando…' : 'Enviar'}</span></button></form>}</> : <div className="comm-no-chat"><FaWhatsapp /><h2>Selecciona una conversación</h2><p>La bandeja compartida muestra mensajes reales conectados por WhatsApp Business.</p><button className="btn-primary" onClick={() => setNewChat(true)}><FaPlus /> Nuevo chat externo</button></div>}</main>
    {infoOpen && selected && <aside className="comm-info-drawer" aria-label="Información del contacto"><header><div><span className="modal-eyebrow">INFORMACIÓN</span><h2>{nameOf(selected)}</h2></div><button type="button" className="icon-close" onClick={() => setInfoOpen(false)} aria-label="Cerrar información"><FiX /></button></header><section><b>Contacto</b><p>{[selected.telefono, linkedEmail(selected)].filter(Boolean).join(" · ") || "Sin teléfono registrado"}</p><p className="comm-drawer-badge">{selected.cliente_id ? 'Cliente vinculado' : selected.lead_id ? 'Lead vinculado' : 'Contacto sin vincular'}</p></section><section><b>Estado de conversación</b><p>{selected.estado === 'closed' ? 'Cerrada' : selected.estado === 'archived' ? 'Archivada' : 'En atención'}</p></section><section><b>Etiquetas</b><button type="button" className={`consulate-action ${selected.es_consulado ? 'marked' : ''}`} onClick={() => patchConversation({ es_consulado: !selected.es_consulado })}><FiBriefcase /> {selected.es_consulado ? '🇨🇴 Consulado' : 'Marcar como Consulado'}</button></section><p className="comm-info-note">Las funciones de vínculo, tareas y archivos se habilitan únicamente cuando tengan una integración persistente disponible.</p></aside>}
    {error && <div className="comm-toast">{error}<button onClick={() => setError('')}><FiX /></button></div>}{newChat && <NewWhatsAppChat onClose={() => setNewChat(false)} onCreated={chat => { setConversations(items => [chat, ...items.filter(item => item.id !== chat.id)]); setSelectedId(chat.id); }} />}
  </section>;
}

*/
}

function messageMetadata(message) {
  return message?.metadata_data || message?.metadata || {};
}
const reactionMessageId = (message) => {
  const metadata = messageMetadata(message);
  const raw = metadata?.raw && typeof metadata.raw === "object" ? metadata.raw : metadata;
  return [message?.external_id, metadata?.wamid, raw?.wamid].find((value) => String(value || "").startsWith("wamid.")) || null;
};

function WhatsAppMedia({ message }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState("");
  const mediaId = messageMetadata(message).media_id;
  const hasRecoverableMedia = message.media_available === true || Boolean(mediaId) || Boolean(message.archivo_url);
  useEffect(() => {
    let objectUrl = "";
    let live = true;
    setUrl("");
    setFailed("");
    if (!hasRecoverableMedia) return undefined;
    axios
      .get(`${API}/messages/${message.id}/media`, {
        ...auth(),
        responseType: "blob",
      })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (live) setUrl(objectUrl);
      })
      .catch((error) => {
        if (!live) return;
        setFailed(error.response?.status === 410 ? "expired" : "unavailable");
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.id, hasRecoverableMedia, mediaId]);
  if (!hasRecoverableMedia) return null;
  if (failed === "expired")
    return (
      <span className="wa-media-history">
        <FaFileAlt />
        <span>
          <b>{message.archivo_nombre || (message.tipo === "audio" ? "Audio histórico" : message.tipo === "image" ? "Imagen histórica" : "Archivo histórico")}</b>
          <small>No se sincronizó antes de que venciera el enlace. Consúltalo en WhatsApp Business.</small>
        </span>
      </span>
    );
  if (failed) return <small className="wa-media-error">No fue posible cargar este archivo. Intenta nuevamente.</small>;
  if (!url) return <small className="wa-media-loading">Cargando archivo…</small>;
  if (message.tipo === "image" || message.tipo === "sticker")
    return (
      <span className="wa-media-preview">
        <a href={url} target="_blank" rel="noreferrer" title="Abrir imagen en tamaño completo">
          <img className="wa-media-image" src={url} alt={message.archivo_nombre || "Imagen enviada por WhatsApp"} />
        </a>
        <a className="wa-media-download" href={url} download={message.archivo_nombre || "imagen-whatsapp"}>
          <FiDownload /> Descargar imagen
        </a>
      </span>
    );
  if (message.tipo === "audio")
    return (
      <span className="wa-media-preview">
        <audio className="wa-media-audio" controls src={url}>
          Audio no compatible.
        </audio>
        <a className="wa-media-download" href={url} download={message.archivo_nombre || "audio-whatsapp"}>
          <FiDownload /> Descargar audio
        </a>
      </span>
    );
  if (message.tipo === "video")
    return (
      <span className="wa-media-preview">
        <video className="wa-media-video" controls src={url} />
        <a className="wa-media-download" href={url} download={message.archivo_nombre || "video-whatsapp"}>
          <FiDownload /> Descargar video
        </a>
      </span>
    );
  return (
    <a className="wa-media-document" href={url} download={message.archivo_nombre || "archivo"}>
      <FaFileAlt />
      <span>
        {message.archivo_nombre || "Documento"}
        <small>Descargar archivo</small>
      </span>
    </a>
  );
}

const WA_EMOJIS = ["👍", "❤️", "😂", "🙏", "✅", "🎉"];
function deliveryError(message) {
  const error = messageMetadata(message).errors?.[0] || {};
  const code = String(error.code || "");
  const detail = error.title || error.details || "";
  if (code === "131047" || /more than 24 hours|24 hours have passed/i.test(detail)) return "Fuera de la ventana de 24 horas. Inicia nuevamente el contacto con la plantilla aprobada.";
  if (code === "131049") return "Meta limitó esta plantilla para este destinatario. No es un bloqueo del plan de YCloud: no la repitas de inmediato y utiliza una plantilla Utility correspondiente al motivo real del contacto.";
  if (/template.*(paused|disabled|rejected)|plantilla.*(pausada|deshabilitada|rechazada)/i.test(detail)) return "Esta plantilla no está disponible actualmente. El CRM actualizará su estado antes del próximo envío.";
  return detail;
}

function showMessageText(message) {
  const content = String(message?.contenido || "").trim();
  if (!content) return true;
  if (!["image", "audio", "video", "document", "sticker"].includes(message.tipo)) return true;
  const generic = /^(📷\s*)?imagen (recibida|enviada)$|^(🎵\s*)?audio (recibido|enviado)$|^(📄\s*)?documento (recibido|enviado)$/i;
  const duplicatedByMedia = generic.test(content) || content === String(message.archivo_nombre || "").trim();
  return !duplicatedByMedia || message.media_available !== true;
}
function visibleMessageContent(message) {
  const content = String(message?.contenido || "").trim();
  if (content) return content;
  const labels = {
    reaction: "Reacción a un mensaje",
    location: "📍 Ubicación compartida",
    contacts: "👤 Contacto compartido",
    interactive: "Respuesta interactiva",
    button: "Respuesta mediante botón",
    sticker: "🏷️ Sticker recibido",
    video: "🎥 Video recibido",
    image: "📷 Imagen recibida",
    audio: "🎵 Audio recibido",
    document: "📄 Documento recibido",
  };
  return labels[message?.tipo] || "Mensaje de WhatsApp sin contenido visible";
}

function isIncompleteHistoryEvent(message) {
  // These are provider bookkeeping rows imported during an early Coexistence
  // history replay. They contain neither a WhatsApp type nor recoverable
  // customer content. Keep them in the audit database, but do not present
  // them as if the customer had sent an empty message.
  if (String(message?.contenido || "").trim() !== "Mensaje de WhatsApp sin contenido visible") return false;
  const metadata = messageMetadata(message);
  const raw = metadata?.raw && typeof metadata.raw === "object" ? metadata.raw : metadata;
  if (!raw || typeof raw !== "object") return true;
  const type = String(raw.type || raw.messageType || message?.tipo || "").trim().toLowerCase();
  const hasPayload = ["text", "image", "video", "document", "audio", "sticker", "reaction", "location", "contacts", "interactive", "button", "edit", "order", "system"]
    .some((key) => raw[key]);
  return !type || type === "unknown" || !hasPayload;
}

const nonEmptyValues = (values) => [...new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean))];
function sharedWhatsAppContacts(message) {
  const metadata = messageMetadata(message);
  const raw = metadata?.raw && typeof metadata.raw === "object" ? metadata.raw : metadata;
  const contacts = Array.isArray(raw?.contacts) ? raw.contacts : Array.isArray(metadata?.contacts) ? metadata.contacts : [];
  return contacts.filter((contact) => contact && typeof contact === "object").map((contact, index) => {
    const name = contact.name && typeof contact.name === "object" ? contact.name : {};
    const displayName = String(name.formatted_name || contact.formatted_name || [name.first_name, name.middle_name, name.last_name].filter(Boolean).join(" ") || contact.name || "Contacto compartido").trim();
    const phones = nonEmptyValues([
      ...(Array.isArray(contact.phones) ? contact.phones.map((item) => typeof item === "object" ? item.phone || item.wa_id || item.waId : item) : []),
      contact.phone,
    ]);
    const emails = nonEmptyValues([
      ...(Array.isArray(contact.emails) ? contact.emails.map((item) => typeof item === "object" ? item.email : item) : []),
      contact.email,
    ]);
    const organization = contact.org && typeof contact.org === "object"
      ? [contact.org.company, contact.org.department, contact.org.title].filter(Boolean).join(" · ")
      : "";
    const addresses = (Array.isArray(contact.addresses) ? contact.addresses : []).map((address) => {
      if (!address || typeof address !== "object") return "";
      return [address.street, [address.city, address.state].filter(Boolean).join(", "), address.zip, address.country].filter(Boolean).join(" · ");
    }).filter(Boolean);
    return { id: `${displayName}-${index}`, displayName, phones, emails, organization, addresses };
  });
}
function SharedWhatsAppContacts({ message }) {
  const contacts = sharedWhatsAppContacts(message);
  if (!contacts.length) return null;
  return <section className="wa-shared-contacts" aria-label="Información del contacto compartido">
    {contacts.map((contact) => <div className="wa-shared-contact-card" key={contact.id}>
      <b>👤 {contact.displayName}</b>
      {contact.phones.map((phone) => <span key={`phone-${phone}`}>WhatsApp: {phone}</span>)}
      {contact.emails.map((email) => <span key={`email-${email}`}>Correo: {email}</span>)}
      {contact.organization && <span>Organización: {contact.organization}</span>}
      {contact.addresses.map((address) => <span key={`address-${address}`}>Dirección: {address}</span>)}
    </div>)}
  </section>;
}
function DeliveryReceipt({ message }) {
  const state = messageMetadata(message).delivery_status;
  if (state === "read")
    return (
      <span className="wa-receipt read" title="Leído">
        <FaCheckDouble />
      </span>
    );
  if (state === "delivered")
    return (
      <span className="wa-receipt" title="Entregado">
        <FaCheckDouble />
      </span>
    );
  if (state === "failed")
    return (
      <span className="wa-receipt failed" title={deliveryError(message) || "Falló el envío"}>
        !
      </span>
    );
  return (
    <span className="wa-receipt" title="Enviado">
      <FaCheck />
    </span>
  );
}

const CHANNELS = {
  whatsapp: {
    label: "WhatsApp",
    icon: FaWhatsapp,
    pending: "WhatsApp requiere confirmar Coexistence antes de recibir o enviar mensajes.",
  },
  instagram: {
    label: "Instagram",
    icon: FaInstagram,
    pending: "Instagram recibirá DMs cuando Meta confirme la cuenta y la suscripción del webhook.",
  },
  messenger: {
    label: "Facebook",
    icon: FaFacebookF,
    pending: "Facebook Messenger recibirá mensajes cuando Meta confirme la Page y la suscripción del webhook.",
  },
};

function ExternalChannelWorkspace({ liteMode = false }) {
  const [channel, setChannel] = useState("whatsapp");
  const [metaStatus, setMetaStatus] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  useEffect(() => {
    let live = true;
    axios
      .get(`${ROOT_API}/meta/status`, auth())
      .then(({ data }) => live && setMetaStatus(data))
      .catch(() => live && setMetaStatus({ services: {} }));
    axios
      .get(`${API}/whatsapp/status`, auth())
      .then(({ data }) => live && setWhatsappStatus(data))
      .catch(() => live && setWhatsappStatus({ connected: false }));
    return () => {
      live = false;
    };
  }, []);
  const availableChannels = Object.entries(CHANNELS).filter(
    ([key]) => key === "whatsapp" || metaStatus?.services?.[key] === "connected",
  );
  return (
    <>
      {availableChannels.length > 1 && <nav className="comm-channel-tabs" aria-label="Canales externos">
        {availableChannels.map(([key, item]) => {
          const Icon = item.icon;
          const connected = key === "whatsapp" ? whatsappStatus?.connected === true : metaStatus?.services?.[key] === "connected";
          const checking = key === "whatsapp" && whatsappStatus === null;
          return (
            <button key={key} type="button" className={channel === key ? "active" : ""} onClick={() => setChannel(key)}>
              <Icon /> {item.label}
              <small>{checking ? "Verificando" : connected ? "Conectado" : "Pendiente"}</small>
            </button>
          );
        })}
      </nav>}
      {channel === "whatsapp" ? <WhatsAppWorkspaceV2 liteMode={liteMode} /> : <MetaChannelInbox channel={channel} connectionState={metaStatus?.services?.[channel] || "configuration_pending"} />}
    </>
  );
}

function MetaChannelInbox({ channel, connectionState }) {
  const channelInfo = CHANNELS[channel];
  const ChannelIcon = channelInfo.icon;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/conversations`, { ...auth(), params: { canal: channel } })
      .then(({ data }) => live && setRows(data || []))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [channel]);
  return (
    <section className="meta-channel-inbox">
      <ChannelIcon />
      <h2>{channelInfo.label}</h2>
      <p>{connectionState === "connected" ? "La conexión está activa. Los mensajes nuevos recibidos por el webhook aparecerán aquí." : channelInfo.pending}</p>
      {loading ? (
        <small>Comprobando conversaciones…</small>
      ) : rows.length ? (
        <div>
          {rows.map((row) => (
            <article key={row.id}>
              <b>{nameOf(row)}</b>
              <small>{row.ultimo_mensaje || "Sin mensajes todavía"}</small>
            </article>
          ))}
        </div>
      ) : (
        <small>No hay conversaciones recibidas todavía.</small>
      )}
    </section>
  );
}

function WhatsAppWorkspaceV2({ liteMode = false }) {
  const [conversations, setConversations] = useState([]),
    [selectedId, setSelectedId] = useState(() => window.history.state?.crmWhatsappConversationId || null),
    [messages, setMessages] = useState([]),
    [pendingMessages, setPendingMessages] = useState([]),
    [templates, setTemplates] = useState([]),
    [draft, setDraft] = useState(""),
    [file, setFile] = useState(null),
    [search, setSearch] = useState(""),
    [messageSearchOpen, setMessageSearchOpen] = useState(false),
    [messageSearch, setMessageSearch] = useState(""),
    [filter, setFilter] = useState("all"),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [messagesReadyFor, setMessagesReadyFor] = useState(null),
    [sending, setSending] = useState(false),
    [newChat, setNewChat] = useState(false),
    [infoOpen, setInfoOpen] = useState(false),
    [emojiOpen, setEmojiOpen] = useState(false),
    [recording, setRecording] = useState(false),
    [voiceFinalizing, setVoiceFinalizing] = useState(false),
    [audioPreviewUrl, setAudioPreviewUrl] = useState(""),
    [tools, setTools] = useState(null),
    [contactName, setContactName] = useState(""),
    [notes, setNotes] = useState([]),
    [noteDraft, setNoteDraft] = useState(""),
    [savingNote, setSavingNote] = useState(false),
    [templateOpen, setTemplateOpen] = useState(false),
    [selectedTemplateName, setSelectedTemplateName] = useState(""),
    [selectedTemplateLanguage, setSelectedTemplateLanguage] = useState(""),
    [templateValues, setTemplateValues] = useState([]),
    [liteStatus, setLiteStatus] = useState(null),
    [isMobile, setIsMobile] = useState(() => window.matchMedia?.("(max-width:760px)").matches),
    [mobileChatOpen, setMobileChatOpen] = useState(() => Boolean(window.history.state?.crmWhatsappConversationId));
  const fileInput = useRef(null),
    recorder = useRef(null),
    chunks = useRef([]),
    thread = useRef(null),
    bottom = useRef(null),
    autoScroll = useRef(true),
    initialScrollPending = useRef(true),
    lastRenderedMessage = useRef(null),
    pressTimer = useRef(null),
    selectedIdRef = useRef(null),
    lastMessageIdRef = useRef(null),
    messageItemsRef = useRef([]),
    messageLoadingForRef = useRef(null),
    searchRef = useRef(""),
    conversationRequest = useRef(0),
    messageRequest = useRef(0),
    noteRequest = useRef(0);
  const selected = conversations.find((item) => String(item.id) === String(selectedId));
  useEffect(() => {
    if (!file?.type?.startsWith("audio/") || !file.size) {
      setAudioPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setAudioPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const displayMessages = useMemo(
    () => oldestFirst([...messages, ...pendingMessages], messageTime)
      .filter((message) => !isIncompleteHistoryEvent(message)),
    [messages, pendingMessages],
  );
  const hiddenHistoryEventCount = messages.filter((message) => isIncompleteHistoryEvent(message)).length;
  const searchedMessages = useMemo(() => {
    const term = normalizeSearchText(messageSearch).trim();
    if (!term) return displayMessages;
    return displayMessages.filter((message) => normalizeSearchText([
      visibleMessageContent(message),
      message.archivo_nombre,
      message.archivo_tipo,
    ].filter(Boolean).join(" ")).includes(term));
  }, [displayMessages, messageSearch]);
  useEffect(() => {
    axios.get(`${ROOT_API}/settings/whatsapp-lite/status`, auth()).then(({ data }) => setLiteStatus(data)).catch(() => setLiteStatus({ status: "unavailable" }));
  }, []);
  const loadConversations = useCallback(async (requestedSearch) => {
    const requestId = ++conversationRequest.current;
    try {
      const searchTerm = String(requestedSearch ?? searchRef.current).trim();
      const { data } = await axios.get(`${API}/conversations`, {
        ...auth(),
        params: { canal: "whatsapp", ...(searchTerm ? { search: searchTerm } : {}) },
      });
      if (requestId !== conversationRequest.current) return;
      const next = newestFirst(Array.isArray(data) ? data : [], conversationTime);
      // The API has already reconciled provider timestamps and duplicate
      // records. Replace the polled snapshot instead of retaining stale rows
      // from an earlier response in browser memory.
      setConversations(next);
      setError((current) => /No fue posible cargar WhatsApp Business\.?$/i.test(current) ? "" : current);
      setSelectedId((current) => (next.some((item) => String(item.id) === String(current)) ? current : next[0]?.id || null));
    } catch (err) {
      setError(err.response?.data?.detail || "No fue posible cargar WhatsApp Business.");
    } finally {
      if (requestId === conversationRequest.current) setLoading(false);
    }
  }, []);
  const loadMessages = useCallback(async (id, afterId = null) => {
    if (!id) {
      if (!selectedIdRef.current) setMessages([]);
      return;
    }
    const conversationKey = String(id);
    // Do not let the five-second poll replace an active initial load.  When
    // Render is briefly slow, repeated requests used to invalidate each other
    // before either could update the screen, leaving a selected chat blank
    // until the user pressed F5.
    if (messageLoadingForRef.current === conversationKey) return;
    messageLoadingForRef.current = conversationKey;
    const requestId = ++messageRequest.current;
    try {
      const { data } = await axios.get(`${API}/messages/${id}`, {
        ...auth(),
        params: { limit: afterId ? 100 : 300, ...(afterId ? { after_id: afterId } : {}) },
      });
      if (requestId !== messageRequest.current || String(id) !== String(selectedIdRef.current)) return;
      const incoming = Array.isArray(data) ? data : [];
      const next = afterId
        ? null
        : oldestFirst(incoming, messageTime);
      if (next) {
        setMessages(next);
      } else if (incoming.length) {
        setMessages((current) => {
          const byId = new Map(current.map((message) => [String(message.id), message]));
          incoming.forEach((message) => byId.set(String(message.id), message));
          return oldestFirst([...byId.values()], messageTime);
        });
      }
      setError((current) => /No se pudo abrir la conversación\.?$/i.test(current) ? "" : current);
      setMessagesReadyFor(id);
      if (incoming.some((message) => message.direccion === "incoming" && !message.leido)) {
        axios.post(`${API}/conversations/${id}/read`, {}, auth()).catch(() => {});
        setConversations((items) => items.map((item) => (String(item.id) === String(id) ? { ...item, mensajes_no_leidos: 0 } : item)));
      }
    } catch (err) {
      if (requestId === messageRequest.current && String(id) === String(selectedIdRef.current)) setError(err.response?.data?.detail || "No se pudo abrir la conversación.");
    } finally {
      if (messageLoadingForRef.current === conversationKey) messageLoadingForRef.current = null;
    }
  }, []);
  const loadDeliveryStatuses = useCallback(async (id) => {
    const messageIds = messageItemsRef.current
      .filter((message) => message.direccion === "outgoing")
      .slice(-50)
      .map((message) => message.id)
      .filter(Boolean);
    if (!id || !messageIds.length) return;
    try {
      const { data } = await axios.get(`${API}/messages/${id}/delivery-statuses`, {
        ...auth(),
        params: { message_id: messageIds },
        // FastAPI expects repeated ``message_id`` parameters, rather than
        // Axios's bracketed default (message_id[]).
        paramsSerializer: { indexes: null },
      });
      if (String(id) !== String(selectedIdRef.current) || !Array.isArray(data) || !data.length) return;
      const receipts = new Map(data.map((item) => [String(item.id), item]));
      setMessages((current) => current.map((message) => {
        const receipt = receipts.get(String(message.id));
        return receipt ? { ...message, metadata: receipt.metadata, leido: receipt.leido, leido_en: receipt.leido_en } : message;
      }));
    } catch {
      // Receipt refresh is auxiliary; do not interrupt an otherwise working chat.
    }
  }, []);
  const loadTemplates = useCallback(
    () =>
      axios
        .get(`${API}/whatsapp/templates`, auth())
        .then(({ data }) => setTemplates(data || []))
        .catch(() => setTemplates([])),
    [],
  );
  const loadNotes = useCallback(async (id) => {
    if (!id) return setNotes([]);
    const requestId = ++noteRequest.current;
    try {
      const { data } = await axios.get(`${API}/conversations/${id}/notes`, {
        ...auth(),
        params: { limit: 20 },
      });
      if (requestId === noteRequest.current && String(id) === String(selectedIdRef.current)) setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestId === noteRequest.current && String(id) === String(selectedIdRef.current)) setError(err.response?.data?.detail || "No se pudieron cargar las notas internas.");
    }
  }, []);
  useEffect(() => {
    loadConversations();
    loadTemplates();
  }, [loadConversations, loadTemplates]);
  useEffect(() => {
    searchRef.current = search;
    const timer = window.setTimeout(() => loadConversations(search), 300);
    return () => window.clearTimeout(timer);
  }, [search, loadConversations]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
    messageRequest.current += 1;
    messageLoadingForRef.current = null;
    autoScroll.current = true;
    initialScrollPending.current = true;
    lastRenderedMessage.current = null;
    lastMessageIdRef.current = null;
    setMessagesReadyFor(null);
    setMessages([]);
    setPendingMessages([]);
    setNotes([]);
    setMessageSearch("");
    setMessageSearchOpen(false);
    loadMessages(selectedId);
    loadNotes(selectedId);
    setInfoOpen(false);
  }, [selectedId, loadMessages, loadNotes]);
  useEffect(() => {
    messageItemsRef.current = messages;
    lastMessageIdRef.current = messages.reduce((latest, message) => Math.max(latest, Number(message.id) || 0), 0) || null;
  }, [messages]);
  useEffect(() => {
    setContactName(selected?.nombre_contacto || "");
  }, [selected?.id, selected?.nombre_contacto]);
  useEffect(() => {
    if (!selectedId || String(messagesReadyFor) !== String(selectedId)) return;
    const newestMessage = messages[messages.length - 1];
    const newestId = newestMessage ? String(newestMessage.id) : null;
    const receivedNewMessage = newestId !== lastRenderedMessage.current;
    if ((initialScrollPending.current || (autoScroll.current && receivedNewMessage)) && bottom.current) {
      bottom.current.scrollIntoView({
        block: "end",
        behavior: initialScrollPending.current ? "auto" : "smooth",
      });
    }
    initialScrollPending.current = false;
    lastRenderedMessage.current = newestId;
  }, [messages, messagesReadyFor, selectedId]);
  useEffect(() => {
    const media = window.matchMedia("(max-width:760px)");
    const update = () => {
      setIsMobile(media.matches);
      if (!media.matches) setMobileChatOpen(true);
    };
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  useEffect(() => {
    // Keep a selected chat responsive without repeatedly transferring its
    // complete history.  The API receives ``after_id`` after the first load,
    // while the broader inbox and notes refresh less often.
    const conversationTimer = window.setInterval(() => { if (!document.hidden) loadConversations(); }, 15000);
    const messageTimer = window.setInterval(() => {
      if (!document.hidden && selectedIdRef.current) {
        loadMessages(selectedIdRef.current, lastMessageIdRef.current);
        loadDeliveryStatuses(selectedIdRef.current);
      }
    }, 5000);
    const noteTimer = window.setInterval(() => {
      if (!document.hidden && selectedIdRef.current) loadNotes(selectedIdRef.current);
    }, 45000);
    const templateTimer = window.setInterval(() => { if (!document.hidden) loadTemplates(); }, 60000);
    return () => {
      clearInterval(conversationTimer);
      clearInterval(messageTimer);
      clearInterval(noteTimer);
      clearInterval(templateTimer);
    };
  }, [loadConversations, loadDeliveryStatuses, loadMessages, loadNotes, loadTemplates]);
  const chooseConversation = (id) => {
    setError("");
    selectedIdRef.current = id;
    setSelectedId(id);
    if (isMobile) {
      const currentState = window.history.state || {};
      if (String(currentState.crmWhatsappConversationId || "") !== String(id)) {
        window.history.pushState({ ...currentState, crmWhatsappConversationId: String(id) }, "", window.location.href);
      }
      setMobileChatOpen(true);
    }
  };
  useEffect(() => {
    const restoreMobileChat = () => {
      if (!isMobile) return;
      const historyConversationId = window.history.state?.crmWhatsappConversationId;
      if (!historyConversationId) {
        setMobileChatOpen(false);
        return;
      }
      selectedIdRef.current = historyConversationId;
      setSelectedId(historyConversationId);
      setMobileChatOpen(true);
    };
    window.addEventListener("popstate", restoreMobileChat);
    return () => window.removeEventListener("popstate", restoreMobileChat);
  }, [isMobile]);
  const onThreadScroll = () => {
    const node = thread.current;
    if (node) autoScroll.current = node.scrollHeight - node.scrollTop - node.clientHeight < 90;
  };
  const filtered = useMemo(
    () =>
      conversations.filter((chat) => {
        const matches = normalizeSearchText([
          nameOf(chat),
          chat.nombre_contacto,
          chat.telefono,
          linkedEmail(chat),
          chat.ultimo_mensaje_contenido,
          chat.search_match_contenido,
          chat.search_match_archivo_nombre,
        ].filter(Boolean).join(" ")).includes(normalizeSearchText(search));
        const matchesFilter = filter === "all" || (filter === "unread" && chat.mensajes_no_leidos > 0) || (filter === "closed" && chat.estado === "closed");
        return matches && matchesFilter;
      }),
    [conversations, search, filter],
  );
  const previewOf = (chat) => {
    const prefixes = { image: "📷 Imagen", audio: "🎙️ Nota de voz", video: "🎥 Video", document: "📄 Documento" };
    if (search.trim() && (chat.search_match_contenido || chat.search_match_archivo_nombre || chat.search_match_tipo)) {
      const match = String(chat.search_match_contenido || chat.search_match_archivo_nombre || prefixes[chat.search_match_tipo] || "Mensaje encontrado").trim();
      return `Coincidencia: ${match}`;
    }
    const content = String(chat.ultimo_mensaje_contenido || "").trim();
    const preview = content || prefixes[chat.ultimo_mensaje_tipo] || "Sin mensajes todavía";
    return `${chat.ultimo_mensaje_direccion === "outgoing" ? "Tú: " : ""}${preview}`;
  };
  const send = async (event) => {
    event.preventDefault();
    const useLite = liteStatus?.status === "connected" && (liteMode || !serviceWindowOpen);
    if (liteMode && liteStatus?.status !== "connected") {
      setError("WhatsApp Lite no está conectado. Un administrador debe vincular el dispositivo desde Configuración → Integraciones.");
      return;
    }
    if (!serviceWindowOpen && !useLite) {
      setError("Fuera de la ventana de 24 horas debes enviar una plantilla aprobada y esperar que el cliente responda.");
      return;
    }
    if ((!draft.trim() && !file) || !selected || sending || recording || voiceFinalizing) return;
    autoScroll.current = true;
    setSending(true);
    const outgoingContent = draft.trim();
    const outgoingFile = file;
    const pendingId = `pending-${Date.now()}`;
    // Show the operator immediately what is queued.  The row is replaced by
    // the persisted API message on success and removed on a real rejection;
    // no message is ever represented as delivered before WhatsApp accepts it.
    setPendingMessages((items) => [...items, {
      id: pendingId,
      direccion: "outgoing",
      canal: "whatsapp",
      tipo: outgoingFile?.type?.startsWith("audio/") ? "audio" : outgoingFile ? "document" : "text",
      contenido: outgoingContent || `📎 ${outgoingFile?.name || "Archivo"}`,
      archivo_nombre: outgoingFile?.name || null,
      archivo_tipo: outgoingFile?.type || null,
      autor_nombre: "Equipo QLF",
      created_at: new Date().toISOString(),
      metadata_data: { delivery_status: "pending" },
    }]);
    setDraft("");
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    try {
      if (outgoingFile) {
        const body = new FormData();
        body.append("conversation_id", String(selected.id));
        body.append("contenido", outgoingContent);
        body.append("archivo", outgoingFile);
        const { data } = await axios.post(useLite ? `${API}/messages/media/lite` : `${API}/messages/media`, body, auth());
        if (!data.success) throw new Error(data.error);
      } else {
        const { data } = await axios.post(
          useLite ? `${API}/messages/lite` : `${API}/messages`,
          {
            conversation_id: selected.id,
            contenido: outgoingContent,
            tipo: "text",
          },
          auth(),
        );
        if (!data.success) throw new Error(data.error);
      }
      setPendingMessages((items) => items.filter((message) => message.id !== pendingId));
      await Promise.all([loadMessages(selected.id, lastMessageIdRef.current), loadConversations()]);
    } catch (err) {
      setPendingMessages((items) => items.filter((message) => message.id !== pendingId));
      setDraft((current) => current || outgoingContent);
      if (outgoingFile) setFile(outgoingFile);
      setError(err.response?.data?.detail || err.message || "No fue posible enviar el mensaje.");
    } finally {
      setSending(false);
    }
  };
  const templateWithStatus = (status, category) => {
    const matching = templates.filter(
      (item) =>
        String(item.status || "").toUpperCase() === status &&
        (!category || String(item.category || "").toUpperCase() === category),
    );
    return (
      TEMPLATE_PRIORITY.map((name) => matching.find((item) => item.name === name)).find(Boolean) || matching[0]
    );
  };
  // Mantiene la plantilla vigente mientras Meta revisa la nueva y cambia de
  // forma automática a la redacción preferida tan pronto quede aprobada.
  const approvedUtilityTemplates = useMemo(
    () => templates.filter(
      (item) => String(item.status || "").toUpperCase() === "APPROVED" && String(item.category || "").toUpperCase() === "UTILITY",
    ),
    [templates],
  );
  const approvedUtilityTemplateLanguages = useMemo(
    () => [...new Set(approvedUtilityTemplates.map((item) => templateLanguageCode(item.language)))],
    [approvedUtilityTemplates],
  );
  const utilityTemplateLanguages = useMemo(
    () => [...new Set(templates
      .filter((item) => String(item.category || "").toUpperCase() === "UTILITY" && ["APPROVED", "PENDING"].includes(String(item.status || "").toUpperCase()))
      .map((item) => templateLanguageCode(item.language)))],
    [templates],
  );
  const availableTemplateLanguageKey = approvedUtilityTemplateLanguages.join("|");
  const activeTemplateLanguage = approvedUtilityTemplateLanguages.includes(selectedTemplateLanguage)
    ? selectedTemplateLanguage
    : approvedUtilityTemplateLanguages.find(isSpanishTemplateLanguage) || approvedUtilityTemplateLanguages[0] || "";
  const approvedTemplatesForLanguage = approvedUtilityTemplates.filter(
    (item) => templateLanguageCode(item.language) === activeTemplateLanguage,
  );
  const preferredUtilityTemplate = (items) =>
    TEMPLATE_PRIORITY.map((name) => items.find((item) => item.name === name)).find(Boolean) || items[0];
  const approvedTemplate = approvedTemplatesForLanguage.find((item) => item.name === selectedTemplateName) || preferredUtilityTemplate(approvedTemplatesForLanguage);
  const pendingTemplate = templateWithStatus("PENDING", "UTILITY");
  useEffect(() => {
    if (!approvedUtilityTemplateLanguages.length) {
      if (selectedTemplateLanguage) setSelectedTemplateLanguage("");
      return;
    }
    if (!approvedUtilityTemplateLanguages.includes(selectedTemplateLanguage)) {
      setSelectedTemplateLanguage(approvedUtilityTemplateLanguages.find(isSpanishTemplateLanguage) || approvedUtilityTemplateLanguages[0]);
    }
  }, [availableTemplateLanguageKey, approvedUtilityTemplateLanguages, selectedTemplateLanguage]);
  useEffect(() => {
    if (approvedTemplate?.name && approvedTemplate.name !== selectedTemplateName) setSelectedTemplateName(approvedTemplate.name);
  }, [activeTemplateLanguage, approvedTemplate?.name, selectedTemplateName]);
  const lastIncoming = [...messages].reverse().find((message) => message.direccion === "incoming");
  const serviceWindowResolved = selected && String(messagesReadyFor) === String(selected.id);
  const locallyResolvedWindow = Boolean(lastIncoming && Date.now() - new Date(`${String(lastIncoming.created_at).replace(/Z$/, "")}Z`).getTime() < 24 * 60 * 60 * 1000);
  const serviceWindowOpen = typeof selected?.ventana_servicio_abierta === "boolean" ? selected.ventana_servicio_abierta : locallyResolvedWindow;
  const lastTemplate = [...messages].reverse().find((message) => messageMetadata(message).template_name && messageMetadata(message).delivery_status !== "failed");
  const templateSentRecently = lastTemplate && Date.now() - messageTime(lastTemplate) < 24 * 60 * 60 * 1000;
  const awaitingReply = !serviceWindowOpen && templateSentRecently && (!lastIncoming || messageTime(lastTemplate) > messageTime(lastIncoming));
  const openTemplate = () => {
    if (!approvedTemplate) return;
    const variables = templateVariables(approvedTemplate);
    if (!variables.length) return sendTemplate([]);
    setTemplateValues(variables.map((_, index) => (index === 0 ? nameOf(selected) : "")));
    setTemplateOpen(true);
  };
  const sendTemplate = async (values = templateValues) => {
    if (!selected || !approvedTemplate || sending) return;
    if (templateVariables(approvedTemplate).some((_, index) => !String(values[index] || "").trim())) {
      setError("Completa todos los datos de la plantilla antes de enviarla.");
      return;
    }
    autoScroll.current = true;
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/messages/template`,
        {
          conversation_id: selected.id,
          template_name: approvedTemplate.name,
          language_code: templateLanguageCode(approvedTemplate.language),
          category: "UTILITY",
          parameters: values.map((value) => String(value).trim()),
        },
        auth(),
      );
      if (!data?.success) throw new Error(data?.error || "WhatsApp no aceptó la plantilla.");
      setTemplateOpen(false);
      await Promise.all([loadMessages(selected.id), loadConversations()]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "No fue posible enviar la plantilla.");
    } finally {
      setSending(false);
    }
  };
  const updateChat = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await axios.put(`${API}/conversations/${selected.id}`, patch, auth());
      setConversations((items) => items.map((item) => (item.id === selected.id ? { ...item, ...data } : item)));
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || "No fue posible actualizar el chat.");
      return null;
    }
  };
  const saveContactName = async (event) => {
    event.preventDefault();
    const name = contactName.trim();
    if (!name) return setError("Escribe un nombre para identificar este contacto en el CRM.");
    const updated = await updateChat({ nombre_contacto: name });
    if (updated) setContactName(updated.nombre_contacto || name);
  };
  const saveInternalNote = async (event) => {
    event.preventDefault();
    const content = noteDraft.trim();
    if (!selected || !content || savingNote) return;
    setSavingNote(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/conversations/${selected.id}/notes`, { contenido: content }, auth());
      setNotes((items) => [data, ...items.filter((item) => item.id !== data.id)]);
      setNoteDraft("");
    } catch (err) {
      setError(err.response?.data?.detail || "No fue posible guardar la nota interna.");
    } finally {
      setSavingNote(false);
    }
  };
  const react = async (emoji) => {
    if (!tools) return;
    try {
      await axios.post(`${API}/messages/${tools.id}/reaction`, { emoji }, auth());
      setTools(null);
      await loadMessages(selectedId);
    } catch (err) {
      setError(err.response?.data?.detail || "No fue posible enviar la reacción.");
    }
  };
  const toggleRecord = async () => {
    if (recording && recorder.current) {
      setVoiceFinalizing(true);
      recorder.current.stop();
      return;
    }
    let stream;
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
        throw new Error("El navegador no admite grabación de audio.");
      }
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredType = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported?.(type));
      const current = preferredType ? new MediaRecorder(stream, { mimeType: preferredType }) : new MediaRecorder(stream);
      chunks.current = [];
      setFile(null);
      setError("");
      current.ondataavailable = (event) => event.data.size && chunks.current.push(event.data);
      current.onstop = () => {
        const recordedType = current.mimeType || "audio/webm";
        const extension = recordedType.startsWith("audio/ogg") ? "ogg" : recordedType.startsWith("audio/mp4") ? "m4a" : "webm";
        const recordedFile = new File(chunks.current, `nota-de-voz-${Date.now()}.${extension}`, { type: recordedType });
        if (recordedFile.size) setFile(recordedFile);
        else setError("La grabación quedó vacía. Intenta grabar la nota de voz de nuevo.");
        stream.getTracks().forEach((track) => track.stop());
        recorder.current = null;
        setRecording(false);
        setVoiceFinalizing(false);
      };
      current.onerror = () => {
        stream.getTracks().forEach((track) => track.stop());
        recorder.current = null;
        setRecording(false);
        setVoiceFinalizing(false);
        setError("La grabación se interrumpió. Intenta grabar la nota de voz de nuevo.");
      };
      recorder.current = current;
      current.start();
      setRecording(true);
    } catch {
      stream?.getTracks().forEach((track) => track.stop());
      setVoiceFinalizing(false);
      setError("No se pudo iniciar la grabación. Comprueba el permiso del micrófono e inténtalo de nuevo.");
    }
  };
  const chatVisible = selected && (!isMobile || mobileChatOpen);
  return (
    <section className={`wa-v2 ${chatVisible ? "has-chat" : ""}`}>
      <aside className="wa-v2-list">
        <header>
          <b>
            <FaWhatsapp /> WhatsApp
          </b>
          <button onClick={() => setNewChat(true)} aria-label="Nuevo chat">
            <FaPlus />
          </button>
        </header>
        <label>
          <FaSearch />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar nombre, número o mensaje" aria-label="Buscar chats por nombre, número o último mensaje" />
        </label>
        <nav>
          {[
            ["all", "Todos"],
            ["unread", "No leídos"],
            ["closed", "Cerrados"],
          ].map(([key, label]) => (
            <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </nav>
        <div>
          {loading && <p>Cargando conversaciones…</p>}
          {!loading && !filtered.length && <p>No hay conversaciones.</p>}
          {filtered.map((chat) => (
            <button key={chat.id} className={`wa-v2-row ${chat.id === selectedId ? "active" : ""}`} onClick={() => chooseConversation(chat.id)}>
              <span className="wa-v2-avatar">{nameOf(chat).charAt(0).toUpperCase()}</span>
              <span>
                <b>{nameOf(chat)}</b>
                <small className="wa-v2-preview" title={previewOf(chat)}>{chat.es_consulado ? "🇨🇴 " : ""}{previewOf(chat)}</small>
              </span>
              <time>{dateTime(search.trim() && chat.search_match_created_at ? chat.search_match_created_at : chat.ultimo_mensaje)}</time>
              {chat.mensajes_no_leidos > 0 && <i>{chat.mensajes_no_leidos}</i>}
            </button>
          ))}
        </div>
      </aside>
      <main className="wa-v2-chat">
        {selected ? (
          <>
            <header>
              <button
                className="wa-v2-back"
                onClick={() => {
                  if (window.history.state?.crmWhatsappConversationId) window.history.back();
                  else setMobileChatOpen(false);
                }}
              >
                <FiChevronLeft />
              </button>
              <span className="wa-v2-avatar">{nameOf(selected).charAt(0).toUpperCase()}</span>
              <div className="wa-v2-contact-heading">
                <b>{nameOf(selected)}</b>
                <small className="wa-v2-contact-meta">
                  <span>{contactStatus(selected)} · {selected.telefono || "Sin teléfono"}</span>
                  {linkedEmail(selected) && <span className="wa-v2-contact-email">{linkedEmail(selected)}</span>}
                </small>
              </div>
              {!!notes.length && (
                <div className="wa-v2-header-notes" aria-label="Últimas notas internas">
                  {notes.slice(0, 2).map((note) => (
                    <span key={note.id} title={`${note.contenido} — ${note.autor_nombre}, ${noteDateTime(note.created_at)}`}>
                      <em>📝 {note.contenido}</em>
                      <time>{note.autor_nombre} · {noteDateTime(note.created_at)}</time>
                    </span>
                  ))}
                </div>
              )}
              <button className="wa-v2-info" onClick={() => setInfoOpen(true)}>
                <FiInfo />
              </button>
              <div className={`wa-v2-message-search ${messageSearchOpen ? "open" : ""}`}>
                {messageSearchOpen && (
                  <input
                    autoFocus
                    value={messageSearch}
                    onChange={(event) => setMessageSearch(event.target.value)}
                    placeholder="Buscar en la conversación"
                    aria-label="Buscar mensajes en esta conversación"
                  />
                )}
                <button
                  type="button"
                  className="wa-v2-info"
                  onClick={() => {
                    if (messageSearchOpen) setMessageSearch("");
                    setMessageSearchOpen((open) => !open);
                  }}
                  title={messageSearchOpen ? "Cerrar búsqueda" : "Buscar en la conversación"}
                  aria-label={messageSearchOpen ? "Cerrar búsqueda en la conversación" : "Buscar en la conversación"}
                >
                  {messageSearchOpen ? <FiX /> : <FaSearch />}
                </button>
              </div>
              <button className="wa-v2-info" onClick={() => setInfoOpen(true)} title="Notas internas del chat" aria-label="Notas internas del chat">
                <FiEdit2 />
              </button>
            </header>
            <section ref={thread} onScroll={onThreadScroll} className="wa-v2-thread">
              {hiddenHistoryEventCount > 0 && (
                <p className="wa-history-import-note">
                  Se omitieron {hiddenHistoryEventCount} evento{hiddenHistoryEventCount === 1 ? "" : "s"} de sincronización sin contenido recuperable.
                </p>
              )}
              {messageSearch.trim() && <p className="wa-v2-search-summary">{searchedMessages.length} resultado{searchedMessages.length === 1 ? "" : "s"} para “{messageSearch.trim()}”.</p>}
              {!searchedMessages.length && messageSearch.trim() && <p className="wa-v2-search-empty">No hay mensajes que coincidan en esta conversación.</p>}
              {searchedMessages.map((message, index) => (
                <React.Fragment key={message.id}>
                  {(!index || new Date(searchedMessages[index - 1].created_at).toDateString() !== new Date(message.created_at).toDateString()) && (
                    <span className="wa-v2-date">
                      {new Intl.DateTimeFormat('en-US', {
                        dateStyle: "medium",
                      }).format(new Date(message.created_at))}
                    </span>
                  )}
                  <article
                    className={`wa-v2-message ${message.direccion === "outgoing" ? "out" : "in"}`}
                    onContextMenu={(event) => {
                      if (event.target.closest(".wa-media-preview, .wa-media-document, .wa-media-history, img, audio, video, a")) return;
                      if (message.direccion !== "incoming" || !reactionMessageId(message)) return;
                      event.preventDefault();
                      setTools(message);
                    }}
                    onTouchStart={(event) => {
                      if (event.target.closest(".wa-media-preview, .wa-media-document, .wa-media-history, img, audio, video, a, button")) return;
                      if (message.direccion !== "incoming" || !reactionMessageId(message)) return;
                      pressTimer.current = setTimeout(() => setTools(message), 550);
                    }}
                    onTouchEnd={() => clearTimeout(pressTimer.current)}
                    onTouchMove={() => clearTimeout(pressTimer.current)}
                    onTouchCancel={() => clearTimeout(pressTimer.current)}
                  >
                    {showMessageText(message) && <p>{visibleMessageContent(message)}</p>}
                    <SharedWhatsAppContacts message={message} />
                    <WhatsAppMedia message={message} />
                    {messageMetadata(message).delivery_status === "failed" && <small className="wa-message-failure">{deliveryError(message) || "WhatsApp rechazó este mensaje."}</small>}
                    <footer>
                      {message.direccion === "outgoing" && <span>Enviado por {message.autor_nombre || "Equipo QLF"}</span>}
                      {message.direccion === "incoming" && reactionMessageId(message) && (
                        <button
                          type="button"
                          className="wa-reaction-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setTools(message);
                          }}
                          aria-label="Reaccionar al mensaje"
                          title="Reaccionar"
                        >
                          <FaSmile />
                        </button>
                      )}
                      <time>
                        {dateTime(message.created_at)} {message.direccion === "outgoing" && <DeliveryReceipt message={message} />}
                      </time>
                    </footer>
                  </article>
                </React.Fragment>
              ))}
              <span ref={bottom} />
            </section>
            {selected.estado === "closed" ? (
              <div className="wa-v2-closed">
                Conversación cerrada. <button onClick={() => updateChat({ estado: "active" })}>Reabrir</button>
              </div>
            ) : (
              <form className="wa-v2-compose" onSubmit={send}>
                {serviceWindowResolved && !serviceWindowOpen && (
                  <div className={`wa-window-note ${awaitingReply ? "sent" : ""}`}>
                    <span>
                      <b>{awaitingReply ? "Plantilla enviada." : "Fuera de la ventana de 24 horas."}</b>{" "}
                      {awaitingReply
                        ? "Esperando que el cliente responda para habilitar mensajes libres por API."
                        : approvedTemplate
                          ? `Plantilla de servicio disponible: ${approvedTemplate.name}.`
                          : pendingTemplate
                            ? "La plantilla de servicio está en revisión de WhatsApp."
                            : "No hay una plantilla Utility aprobada; no se usará Marketing como reemplazo."}
                    </span>
                    <span className="wa-window-actions">
                      {!awaitingReply && approvedTemplate && (
                        <>
                          {utilityTemplateLanguages.length > 1 && (
                            <label className="wa-template-language">
                              <span>Idioma</span>
                              <select
                                value={activeTemplateLanguage}
                                onChange={(event) => {
                                  setSelectedTemplateLanguage(event.target.value);
                                  setSelectedTemplateName("");
                                }}
                                aria-label="Idioma de la plantilla"
                              >
                                {utilityTemplateLanguages.map((language) => (
                                  <option key={language} value={language} disabled={!approvedUtilityTemplateLanguages.includes(language)}>
                                    {templateLanguageLabel(language)}{approvedUtilityTemplateLanguages.includes(language) ? "" : " · en revisión"}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          {approvedTemplatesForLanguage.length > 1 && (
                            <select value={approvedTemplate.name} onChange={(event) => setSelectedTemplateName(event.target.value)} aria-label="Plantilla de servicio">
                              {approvedTemplatesForLanguage.map((template) => (
                                <option key={template.name} value={template.name}>
                                  {UTILITY_TEMPLATE_LABELS[template.name] || template.name.replaceAll("_", " ")}
                                </option>
                              ))}
                            </select>
                          )}
                          <button type="button" onClick={openTemplate} disabled={sending}>
                            {sending ? "Enviando…" : "Preparar plantilla"}
                          </button>
                        </>
                      )}
                      {liteStatus?.status === "connected" && <small>Dispositivo vinculado disponible: puedes escribir texto libre sin cerrar YCloud.</small>}
                    </span>
                  </div>
                )}
                <input ref={fileInput} type="file" hidden accept="image/*,audio/*,video/*,.txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                <button type="button" disabled={!serviceWindowOpen && liteStatus?.status !== "connected"} onClick={() => fileInput.current?.click()} title="Adjuntar">
                  <FaPaperclip />
                </button>
                <button type="button" disabled={!serviceWindowOpen && liteStatus?.status !== "connected"} onClick={() => setEmojiOpen((open) => !open)} title="Emojis">
                  <FaSmile />
                </button>
                <button type="button" disabled={voiceFinalizing || (!recording && !serviceWindowOpen && liteStatus?.status !== "connected")} className={recording ? "recording" : ""} onClick={toggleRecord} title={recording ? "Detener nota de voz" : "Grabar nota de voz"}>
                  {recording ? <FaStop /> : <FaMicrophone />}
                </button>
                <div>
                  {file && (
                    <small>
                      {file.type.startsWith("audio/") ? "🎙️ Audio listo: pulsa Enviar" : `📎 ${file.name}`}
                      <button type="button" onClick={() => setFile(null)}>
                        ×
                      </button>
                    </small>
                  )}
                  {audioPreviewUrl && <audio controls src={audioPreviewUrl} aria-label="Escuchar nota de voz antes de enviarla" />}
                  <textarea
                    rows={1}
                    disabled={!serviceWindowOpen && liteStatus?.status !== "connected"}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                    placeholder={serviceWindowOpen ? (recording ? "Grabando nota de voz…" : "Escribe un mensaje") : liteStatus?.status === "connected" ? "Enviar texto por dispositivo vinculado" : "Esperando respuesta del cliente"}
                  />
                </div>
                <button className="wa-v2-send" title={file?.type?.startsWith("audio/") ? "Enviar nota de voz" : "Enviar mensaje"} disabled={(!serviceWindowOpen && liteStatus?.status !== "connected") || sending || recording || voiceFinalizing || (!draft.trim() && !file)}>
                  <FaPaperPlane />
                </button>
                {emojiOpen && (
                  <aside className="wa-v2-emojis">
                    {WA_EMOJIS.map((emoji) => (
                      <button
                        type="button"
                        key={emoji}
                        onClick={() => {
                          setDraft((value) => `${value}${emoji}`);
                          setEmojiOpen(false);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </aside>
                )}
              </form>
            )}
          </>
        ) : (
          <div className="wa-v2-empty">
            <FaWhatsapp />
            <h2>Selecciona una conversación</h2>
            <button onClick={() => setNewChat(true)}>Nuevo chat</button>
          </div>
        )}
      </main>
      {tools && (
        <div className="wa-v2-tools" onClick={() => setTools(null)}>
          <section onClick={(event) => event.stopPropagation()}>
            <b>Acciones del mensaje</b>
            {tools.direccion === "incoming" && reactionMessageId(tools) ? (
              <div>
                {WA_EMOJIS.map((emoji) => (
                  <button key={emoji} onClick={() => react(emoji)}>
                    {emoji}
                  </button>
                ))}
              </div>
            ) : (
              <small>Las reacciones requieren un mensaje entrante confirmado por WhatsApp.</small>
            )}
            <p>Eliminar para todos no está disponible mediante la API oficial; no se simula esta acción.</p>
            <button onClick={() => setTools(null)}>Cerrar</button>
          </section>
        </div>
      )}
      {templateOpen && approvedTemplate && (
        <div className="wa-template-backdrop" onMouseDown={() => !sending && setTemplateOpen(false)}>
          <form
            className="wa-template-modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              sendTemplate(templateValues);
            }}
          >
            <header>
              <div>
                <small>PLANTILLA DE SERVICIO</small>
                <h2>{UTILITY_TEMPLATE_LABELS[approvedTemplate.name] || "Enviar plantilla de servicio"}</h2>
              </div>
              <button type="button" onClick={() => setTemplateOpen(false)} aria-label="Cerrar"><FiX /></button>
            </header>
            <p className="wa-template-preview">{templateBody(approvedTemplate)}</p>
            <div className="wa-template-fields">
              {templateVariables(approvedTemplate).map((variable, index) => (
                <label key={variable}>
                  {templateFieldLabel(variable, index, approvedTemplate.name)}
                  <input
                    required
                    value={templateValues[index] || ""}
                    onChange={(event) => setTemplateValues((values) => values.map((value, position) => position === index ? event.target.value : value))}
                    placeholder={index === 0 ? "Ej. Angela" : "Completa este dato"}
                  />
                </label>
              ))}
            </div>
            <footer>
              <button type="button" onClick={() => setTemplateOpen(false)}>Cancelar</button>
              <button disabled={sending}>{sending ? "Enviando…" : "Enviar por WhatsApp"}</button>
            </footer>
          </form>
        </div>
      )}
      {infoOpen && (
        <aside className="wa-v2-drawer">
          <button onClick={() => setInfoOpen(false)}>
            <FiX />
          </button>
          <span className="wa-v2-avatar big">{nameOf(selected).charAt(0).toUpperCase()}</span>
          <h2>{nameOf(selected)}</h2>
          <p>{[selected.telefono, linkedEmail(selected)].filter(Boolean).join(" · ") || "Sin teléfono registrado"}</p>
          <section>
            <b>Información del chat</b>
            <p>{selected.cliente_id ? "Cliente vinculado" : selected.lead_id ? "Lead vinculado" : "Contacto sin vincular"}</p>
            <p>{selected.es_consulado ? "🇨🇴 Consulado" : "Conversación general"}</p>
          </section>
          {!selected.cliente_id && !selected.lead_id && (
            <section className="wa-contact-name">
              <b>Nombre visible en el CRM</b>
              <form onSubmit={saveContactName}>
                <input value={contactName} onChange={(event) => setContactName(event.target.value)} maxLength={200} placeholder="Ej. María González" aria-label="Nombre visible en el CRM" />
                <button disabled={!contactName.trim()}>Guardar</button>
              </form>
              <small>Este nombre es interno: no modifica el perfil ni el nombre del contacto en WhatsApp.</small>
            </section>
          )}
          <section className="wa-internal-notes">
            <b>Notas internas del chat</b>
            <form onSubmit={saveInternalNote}>
              <textarea
                rows={2}
                maxLength={240}
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                placeholder="Ej. Se llamó; no respondió"
                aria-label="Nueva nota interna"
              />
              <button disabled={!noteDraft.trim() || savingNote}>{savingNote ? "Guardando…" : "Guardar nota"}</button>
            </form>
            <small>Solo la ve el equipo del CRM; no se envía por WhatsApp.</small>
            <div className="wa-internal-note-list">
              {notes.map((note) => (
                <article key={note.id}>
                  <p>{note.contenido}</p>
                  <time>{note.autor_nombre} · {noteDateTime(note.created_at)}</time>
                </article>
              ))}
              {!notes.length && <p className="wa-notes-empty">Todavía no hay notas internas.</p>}
            </div>
          </section>
          <section>
            <b>Foto de perfil</b>
            <p>La API oficial no expone fotos de perfil de contactos; se muestra su inicial.</p>
          </section>
          <button onClick={() => updateChat({ es_consulado: !selected.es_consulado })}>{selected.es_consulado ? "Quitar marca Consulado" : "Marcar como Consulado"}</button>
          <button
            onClick={() =>
              updateChat({
                estado: selected.estado === "closed" ? "active" : "closed",
              })
            }
          >
            {selected.estado === "closed" ? "Reabrir conversación" : "Cerrar conversación"}
          </button>
        </aside>
      )}
      {error && (
        <div className="wa-v2-error">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}
      {newChat && (
        <NewWhatsAppChat
          onClose={() => setNewChat(false)}
          onCreated={(chat) => {
            setConversations((items) => [chat, ...items.filter((item) => item.id !== chat.id)]);
            chooseConversation(chat.id);
          }}
        />
      )}
    </section>
  );
}

function InternalConversationModal({ users, onClose, onCreated }) {
  const [ids, setIds] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toggle = (id) => setIds((value) => (value.includes(id) ? value.filter((item) => item !== id) : [...value, id]));
  const submit = async (event) => {
    event.preventDefault();
    if (!ids.length) return setError("Selecciona por lo menos un usuario.");
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/internal/conversations`, { participant_ids: ids, nombre: name }, auth());
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear la conversación.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="comm-modal-backdrop" onMouseDown={onClose}>
      <form className="comm-modal internal-modal" onMouseDown={(event) => event.stopPropagation()} onSubmit={submit}>
        <header>
          <div>
            <span className="modal-eyebrow">QLF CONECTA</span>
            <h2>Nuevo mensaje interno</h2>
            <p>Selecciona una persona para un mensaje directo o varias para un grupo privado.</p>
          </div>
          <button type="button" className="icon-close" onClick={onClose}>
            <FiX />
          </button>
        </header>
        <label>
          Nombre del grupo <small>(opcional)</small>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ej. Seguimiento audiencia" />
        </label>
        <div className="internal-user-picker">
          <b>Usuarios registrados</b>
          {users.map((user) => (
            <label key={user.id} className="internal-user-option">
              <input type="checkbox" checked={ids.includes(user.id)} onChange={() => toggle(user.id)} />
              <span className="comm-avatar">{(user.nombre || user.email).charAt(0).toUpperCase()}</span>
              <span>
                {`${user.nombre || ""} ${user.apellido || ""}`.trim() || user.email}
                <small>{user.rol || "Usuario"}</small>
              </span>
            </label>
          ))}
        </div>
        {error && <p className="comm-error">{error}</p>}
        <footer>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={busy}>
            {busy ? "Creando…" : "Abrir conversación"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Attachment({ message }) {
  if (!message.archivo_url) return null;
  const url = `${FILE_ROOT}${message.archivo_url}`;
  if (message.tipo === "image")
    return (
      <a className="internal-image" href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={message.archivo_nombre || "Imagen adjunta"} />
      </a>
    );
  if (message.tipo === "audio")
    return (
      <audio className="internal-audio" controls src={url}>
        Tu navegador no reproduce esta nota de voz.
      </audio>
    );
  if (message.tipo === "video") return <video className="internal-video" controls src={url} />;
  return (
    <a className="internal-file" href={url} target="_blank" rel="noreferrer">
      <FaFileAlt />
      <span>
        {message.archivo_nombre || "Archivo adjunto"}
        <small>Descargar o abrir</small>
      </span>
    </a>
  );
}

const CHAT_REACTIONS = ["👍", "❤️", "😂", "🎉", "🙏", "👀", "✅"];
function InternalMessageBubble({ message, me, onReact, onDelete, pickerId, setPickerId }) {
  const mine = Number(message.usuario_id) === Number(me.id);
  const role = String(me.rol || "").toLowerCase();
  const canDelete = mine || ["administrador", "admin", "managing attorney", "attorney"].includes(role);
  const readers = (message.leido_por || []).filter((item) => Number(item.usuario_id) !== Number(me.id));
  const deleted = message.eliminado_para_todos;
  return (
    <article className={`team-bubble ${mine ? "mine" : "received"} ${deleted ? "deleted" : ""}`} key={message.id}>
      <div className="team-author">
        <span>{mine ? "Tú" : message.autor_nombre || "Miembro del equipo"}</span>
        <time>{dateTime(message.created_at)}</time>
      </div>
      {deleted ? (
        <p className="deleted-message">🚫 Este mensaje fue eliminado para todos.</p>
      ) : (
        <>
          <div className="message-tools">
            <button type="button" onClick={() => setPickerId(pickerId === message.id ? null : message.id)} title="Reaccionar">
              ☺
            </button>
            {canDelete && (
              <button type="button" className="delete-message" onClick={() => onDelete(message.id)} title="Eliminar para todos">
                🗑
              </button>
            )}
          </div>
          {message.contenido && <p>{message.contenido}</p>}
          <Attachment message={message} />
          {pickerId === message.id && (
            <div className="reaction-picker">
              {CHAT_REACTIONS.map((emoji) => (
                <button type="button" key={emoji} onClick={() => onReact(message.id, emoji)}>
                  {emoji}
                </button>
              ))}
            </div>
          )}
          <div className="message-reactions">
            {(message.reacciones || []).map((reaction) => (
              <button type="button" key={reaction.emoji} onClick={() => onReact(message.id, reaction.emoji)} title={reaction.usuarios.map((user) => user.nombre).join(", ")}>
                {reaction.emoji} <small>{reaction.cantidad}</small>
              </button>
            ))}
          </div>
        </>
      )}
      {mine && !deleted && <small className="read-receipt">{readers.length ? `✓✓ Leído por ${readers.map((item) => item.nombre).join(", ")}` : "✓ Enviado"}</small>}
    </article>
  );
}

export function TeamChat() {
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [newChat, setNewChat] = useState(false);
  const [recording, setRecording] = useState(false);
  const [reactionPicker, setReactionPicker] = useState(null);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const fileInput = useRef(null);
  const thread = useRef(null);
  const end = useRef(null);
  const autoScroll = useRef(true);
  const me = currentUser();
  const clearAttachment = useCallback(() => {
    setAttachment(null);
    setAttachmentPreview((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return "";
    });
    if (fileInput.current) fileInput.current.value = "";
  }, []);
  const chooseAttachment = useCallback(
    (file) => {
      clearAttachment();
      if (!file) return;
      setAttachment(file);
      if (file.type.startsWith("audio/")) setAttachmentPreview(URL.createObjectURL(file));
    },
    [clearAttachment],
  );
  useEffect(
    () => () => {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    },
    [attachmentPreview],
  );
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/internal/conversations`, auth());
      setConversations(data.conversations || []);
      setSelectedId((current) => current || data.conversations?.[0]?.id || null);
    } catch (err) {
      setError(err.response?.data?.detail || `No fue posible cargar QLF Conecta (${err.response?.status || "sin conexión"}).`);
    }
  }, []);
  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ROOT_API}/auth/usuarios`, auth());
      setUsers((data || []).filter((user) => user.activo && Number(user.id) !== Number(me.id)));
    } catch {
      setError("No se pudo cargar la lista de usuarios registrados.");
    }
  }, [me.id]);
  const loadMessages = useCallback(async (id, initial = false) => {
    if (!id) return setMessages([]);
    if (initial) setLoadingThread(true);
    try {
      const { data } = await axios.get(`${API}/internal/conversations/${id}/messages`, auth());
      const next = data.messages || [];
      setMessages((previous) => (previous.length === next.length && previous.every((item, index) => item.id === next[index]?.id && JSON.stringify(item.leido_por || []) === JSON.stringify(next[index]?.leido_por || [])) ? previous : next));
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo abrir esta conversación.");
    } finally {
      if (initial) setLoadingThread(false);
    }
  }, []);
  useEffect(() => {
    loadConversations();
    loadUsers();
  }, [loadConversations, loadUsers]);
  useEffect(() => {
    autoScroll.current = true;
    loadMessages(selectedId, true);
  }, [selectedId, loadMessages]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      loadConversations();
      if (selectedId) loadMessages(selectedId);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadConversations, loadMessages, selectedId]);
  useEffect(() => {
    if (autoScroll.current && end.current) end.current.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages.length, selectedId]);
  const selected = conversations.find((item) => item.id === selectedId);
  const onThreadScroll = () => {
    const node = thread.current;
    if (node) autoScroll.current = node.scrollHeight - node.scrollTop - node.clientHeight < 90;
  };
  const send = async (event) => {
    event.preventDefault();
    const content = draft.trim();
    if ((!content && !attachment) || !selectedId || sending) return;
    setSending(true);
    try {
      const data = new FormData();
      data.append("contenido", content);
      if (attachment) data.append("archivo", attachment);
      await axios.post(`${API}/internal/conversations/${selectedId}/messages`, data, auth());
      setDraft("");
      clearAttachment();
      autoScroll.current = true;
      await Promise.all([loadMessages(selectedId), loadConversations()]);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  };
  const reactToMessage = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/internal/messages/${messageId}/reactions`, { emoji }, auth());
      setReactionPicker(null);
      await loadMessages(selectedId);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo registrar la reacción.");
    }
  };
  const deleteMessage = async (messageId) => {
    if (!window.confirm("¿Eliminar este mensaje para todos? Se conservará únicamente el registro de auditoría.")) return;
    try {
      await axios.delete(`${API}/internal/messages/${messageId}`, auth());
      setReactionPicker(null);
      await loadMessages(selectedId);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo eliminar el mensaje.");
    }
  };
  const deleteChat = async () => {
    if (!selected || selected.es_general || !window.confirm("¿Eliminar este chat de la vista? El historial quedará protegido en auditoría.")) return;
    try {
      await axios.delete(`${API}/internal/conversations/${selected.id}`, auth());
      setSelectedId(null);
      setMessages([]);
      await loadConversations();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo eliminar el chat.");
    }
  };
  const toggleRecording = async () => {
    if (recording && mediaRecorder.current) {
      mediaRecorder.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunks.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) audioChunks.current.push(event.data);
      };
      recorder.onstop = () => {
        chooseAttachment(
          new File(audioChunks.current, `nota-de-voz-${Date.now()}.webm`, {
            type: recorder.mimeType || "audio/webm",
          }),
        );
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
      };
      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setError("No se pudo usar el micrófono. Verifica el permiso del navegador e inténtalo de nuevo.");
    }
  };
  return (
    <section className="team-chat">
      <header className="team-chat-header">
        <div>
          <span className="modal-eyebrow">OPERACIONES · EQUIPO</span>
          <h2>
            <FaUsers /> QLF Conecta
          </h2>
          <p>Mensajes internos, conversaciones privadas y grupos del equipo.</p>
        </div>
        <div>
          <button
            className="refresh-team"
            onClick={() => {
              loadConversations();
              if (selectedId) loadMessages(selectedId);
            }}
          >
            <FiRefreshCw /> Actualizar
          </button>
          <button className="btn-primary" onClick={() => setNewChat(true)}>
            <FaPlus /> Nuevo mensaje
          </button>
        </div>
      </header>
      <div className={`internal-chat-layout${mobileThreadOpen ? ' show-thread' : ''}`}>
        <aside className="internal-chat-list">
          <b>Conversaciones</b>
          {conversations.map((chat) => (
            <button key={chat.id} className={chat.id === selectedId ? "selected" : ""} onClick={() => { setSelectedId(chat.id); setMobileThreadOpen(true); }}>
              <span className="comm-avatar">{chat.es_general ? "Q" : (chat.nombre || "C").charAt(0)}</span>
              <span>
                <strong>{chat.es_general ? "💬 Equipo QLF" : chat.nombre}</strong>
                <small>{chat.ultimo_mensaje || (chat.es_general ? "Canal para todos los usuarios" : "Sin mensajes todavía")}</small>
              </span>
              {chat.no_leidos > 0 && <i>{chat.no_leidos}</i>}
            </button>
          ))}
        </aside>
        <main className="internal-thread-panel">
          {selected ? (
            <>
              <header className="internal-thread-head">
                <button type="button" className="internal-mobile-back" aria-label="Volver a conversaciones" onClick={() => setMobileThreadOpen(false)}>‹</button>
                <div>
                  <strong>{selected.es_general ? "💬 Equipo QLF" : selected.nombre}</strong>
                  <small>{selected.es_general ? "Todos los usuarios registrados pueden participar" : selected.participantes?.map((item) => item.nombre).join(" · ")}</small>
                </div>
                {!selected.es_general && (
                  <button type="button" className="delete-chat" onClick={deleteChat}>
                    🗑 Eliminar chat
                  </button>
                )}
              </header>
              <div ref={thread} onScroll={onThreadScroll} className="team-thread">
                {loadingThread && <p className="comm-empty">Cargando conversación…</p>}
                {!loadingThread && !messages.length && <p className="comm-empty">Aún no hay mensajes. Inicia la conversación.</p>}
                {messages.map((message) => (
                  <InternalMessageBubble key={message.id} message={message} me={me} onReact={reactToMessage} onDelete={deleteMessage} pickerId={reactionPicker} setPickerId={setReactionPicker} />
                ))}
                <span ref={end} />
              </div>
              <form className="team-compose" onSubmit={send}>
                <input ref={fileInput} type="file" className="hidden-file-input" onChange={(event) => chooseAttachment(event.target.files?.[0] || null)} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                <button type="button" className="attach-button" onClick={() => fileInput.current?.click()} title="Adjuntar imagen, documento o archivo">
                  <FaPaperclip />
                </button>
                <button type="button" className={`record-button ${recording ? "recording" : ""}`} onClick={toggleRecording} title={recording ? "Detener grabación" : "Grabar nota de voz"}>
                  {recording ? <FaStop /> : <FaMicrophone />}
                </button>
                <div className="compose-copy">
                  {attachment && (
                    <span className="attachment-chip">
                      {attachment.type.startsWith("image/") ? <FaImage /> : <FaFileAlt />} {attachment.name}
                      <button type="button" onClick={clearAttachment}>
                        ×
                      </button>
                    </span>
                  )}
                  {attachmentPreview && (
                    <div className="voice-preview">
                      <span>🎙️ Vista previa</span>
                      <audio controls src={attachmentPreview}>
                        Tu navegador no reproduce esta nota de voz.
                      </audio>
                    </div>
                  )}
                  <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={recording ? "Grabando nota de voz…" : "Escribe un mensaje"} />
                </div>
                <button className="send-internal" disabled={(!draft.trim() && !attachment) || sending || recording}>
                  <FaPaperPlane /> {sending ? "Enviando…" : "Enviar"}
                </button>
              </form>
            </>
          ) : (
            <div className="comm-no-chat">
              <FaUsers />
              <h2>Elige una conversación</h2>
              <p>Puedes escribir al equipo completo o iniciar un mensaje directo.</p>
            </div>
          )}
        </main>
      </div>
      {error && (
        <div className="comm-toast">
          {error}
          <button onClick={() => setError("")}>
            <FiX />
          </button>
        </div>
      )}
      {newChat && (
        <InternalConversationModal
          users={users}
          onClose={() => setNewChat(false)}
          onCreated={(conversation) => {
            setConversations((items) => [conversation, ...items.filter((item) => item.id !== conversation.id)]);
            setSelectedId(conversation.id);
            setMobileThreadOpen(true);
          }}
        />
      )}
    </section>
  );
}

export default function CommunicationHub({ liteMode = false }) {
  return (
    <div className="communication-hub">
      <h1 className="comm-visually-hidden">{liteMode ? "WhatsApp Lite" : "Comunicaciones"}</h1>
      {liteMode && <div className="wa-lite-mode-banner"><b>WhatsApp Lite · dispositivo vinculado</b><span>Comparte contactos, nombres, notas e historial con Comunicaciones. Los mensajes de texto se envían por el dispositivo QR cuando está conectado; YCloud permanece activo.</span></div>}
      <WhatsAppWorkspace liteMode={liteMode} />
    </div>
  );
}
